import { ConfigProvider } from 'antd'
import thTH from 'antd/locale/th_TH'
import dayjs from 'dayjs'
import 'dayjs/locale/th'
import buddhistEra from 'dayjs/plugin/buddhistEra'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import localeData from 'dayjs/plugin/localeData'
import utc from 'dayjs/plugin/utc'
import weekday from 'dayjs/plugin/weekday'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { Provider } from 'react-redux'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'

import { useEffect } from 'react'
import { store } from '../../store'
import defineAbilityFor, { AbilityContext } from '../components/Ability'
import DefaultLayout from '../components/Layouts/DefaultLayout'
import { useUser } from '../hooks/useUser'
import themeConfig from '../themes'
import { IUser } from '../types/user'
import CategoriesPage from './Category'
import ChatPage from './Chat'
import CRMPage from './CRM'
import CustomersPage from './Customer'
import DashboardPage from './Dashboard'
import ExpensesPage from './Expenses'
import LoginPage from './Login'
import Logout from './Logout'
import MarketplacePage from './Marketplace'
import OrdersPage from './Order'
import ProductsPage from './Product'
import ProductGroupsPage from './ProductGroup'
import ReportsPage from './Report'
import SettingPage from './Setting'
import StockPage from './Stock'
import TrackingPage from './Tracking'
import UsersPage from './Users'
import FacebookPage from './Facebook'
import ChatsPage from './Chats'
import BroadCastPage from './Broadcast'
import OrdersPlatformsPage from './Platforms'
import SplashLoadingScreen from '../components/Loading/SplashLoadingScreen'
import AiPage from './Ai'
import SalepagePage from './Salepage'

dayjs.extend(utc)
dayjs.extend(weekday)
dayjs.extend(buddhistEra)
dayjs.extend(isSameOrAfter)
dayjs.extend(localeData)
dayjs.extend(duration)
dayjs.extend(isBetween)
dayjs.locale('th')

const ScrollToTop = () => {
  const location = useLocation()

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [location])

  return null
}

const App = () => {
  const { isAuthenticated = false, user, merchant, isLoading } = useUser()
  const query = new URLSearchParams(window.location.search)
  const isPopup = query.get('display') === 'popup'

  if (isLoading) {
    return <SplashLoadingScreen />
  }

  return (
    <Provider store={store}>
      <BrowserRouter>
        <ScrollToTop />
        <ConfigProvider theme={themeConfig} locale={thTH}>
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITEKEY ?? ''}
          >
            {isAuthenticated ? (
              <AbilityContext.Provider value={defineAbilityFor(user as IUser)}>
                <DefaultLayout isPopup={isPopup}>
                  <Routes>
                    <Route path="popup/*" element={<SettingPage />} />
                    <Route index element={<DashboardPage />} />
                    {merchant?.config?.openCRM && (
                      <Route path="crm/*" element={<CRMPage />} />
                    )}
                    <Route path="ai/*" element={<AiPage />} />
                    <Route path="reports/*" element={<ReportsPage />} />
                    <Route path="salepage/*" element={<SalepagePage />} />
                    <Route path="users/*" element={<UsersPage />} />
                    {/* <Route path="merchants/*" element={<MerchantPage />} /> */}
                    <Route path="expenses/*" element={<ExpensesPage />} />
                    <Route path="setting/*" element={<SettingPage />} />
                    <Route path="products/*" element={<ProductsPage />} />
                    <Route path="stock/*" element={<StockPage />} />
                    <Route path="tracking/*" element={<TrackingPage />} />
                    <Route path="categories/*" element={<CategoriesPage />} />
                    <Route
                      path="product-groups/*"
                      element={<ProductGroupsPage />}
                    />
                    <Route path="orders/*" element={<OrdersPage />} />
                    <Route
                      path="platforms/*"
                      element={<OrdersPlatformsPage />}
                    />
                    <Route path="logout/*" element={<Logout />} />
                    {merchant?.config?.openChat && (
                      <>
                        <Route path="chat/*" element={<ChatPage />} />
                        <Route path="chats/*" element={<ChatsPage />} />
                      </>
                    )}
                    <Route path="broadcast/*" element={<BroadCastPage />} />
                    <Route path="marketplace/*" element={<MarketplacePage />} />
                    <Route path="customers/*" element={<CustomersPage />} />
                    <Route path="facebook/*" element={<FacebookPage />} />
                  </Routes>
                </DefaultLayout>
              </AbilityContext.Provider>
            ) : (
              <Routes>
                <Route path="/*" element={<LoginPage message="unauthen" />} />
              </Routes>
            )}
          </GoogleReCaptchaProvider>
        </ConfigProvider>
      </BrowserRouter>
    </Provider>
  )
}
export default App
